import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, FinancialConnectionsSheet } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config';
import StripeAutopaySetup from '../financial/StripeAutopaySetup';
import './TenantPortal.css';

let stripePromise = null;

// Initialize Stripe
const getStripePromise = async () => {
    if (!stripePromise) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/stripe/config`);
            const config = await response.json();
            stripePromise = loadStripe(config.publishableKey);
        } catch (error) {
            console.error('Error loading Stripe config:', error);
        }
    }
    return stripePromise;
};

// Autopay Card Form Component
const AutopayCardForm = ({ onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);

        const cardElement = elements.getElement(CardElement);
        
        // Create payment method
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        });

        if (pmError) {
            onError(pmError.message);
            setProcessing(false);
            return;
        }

        // Success - payment method created
        onSuccess(paymentMethod.id, 'card');
        setProcessing(false);
    };

    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    return (
        <form onSubmit={handleSubmit} className="autopay-card-form">
            <div className="stripe-card-element" style={{ marginBottom: '20px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                <CardElement options={cardElementOptions} />
            </div>
            <div className="autopay-disclaimer" style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: '#666' }}>🔒 Your card information is securely processed by Stripe. We never store your card details.</p>
            </div>
            <div className="modal-actions">
                <button 
                    type="submit" 
                    disabled={!stripe || processing}
                    className="submit-btn"
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#635BFF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        cursor: processing || !stripe ? 'not-allowed' : 'pointer',
                        opacity: processing || !stripe ? 0.6 : 1
                    }}
                >
                    {processing ? 'Processing...' : '💳 Save Card for Autopay'}
                </button>
            </div>
        </form>
    );
};

// Stripe Payment Form Component
const StripePaymentForm = ({ paymentId, amount, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);

    useEffect(() => {
        if (paymentId && stripe) {
            // Create payment intent
            fetch(`${API_BASE_URL}/api/stripe/create-payment-intent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ payment_id: paymentId })
            })
            .then(res => res.json())
            .then(data => {
                if (data.client_secret) {
                    setClientSecret(data.client_secret);
                } else if (data.error) {
                    onError(data.error);
                }
            })
            .catch(err => onError(err.message));
        }
    }, [paymentId, stripe]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);

        const cardElement = elements.getElement(CardElement);
        
        // Create payment method
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        });

        if (pmError) {
            onError(pmError.message);
            setProcessing(false);
            return;
        }

        // Confirm payment intent
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethod.id,
        });

        if (confirmError) {
            onError(confirmError.message);
        } else if (paymentIntent.status === 'succeeded') {
            onSuccess(paymentIntent);
        }
        
        setProcessing(false);
    };

    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    return (
        <form onSubmit={handleSubmit} className="stripe-payment-form">
            <div className="stripe-card-element">
                <CardElement options={cardElementOptions} />
            </div>
            <button 
                type="submit" 
                disabled={!stripe || !clientSecret || processing}
                className="stripe-submit-btn"
            >
                {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
            </button>
        </form>
    );
};

const TenantPortal = () => {
    const [tenantData, setTenantData] = useState(null);
    const [maintenanceForm, setMaintenanceForm] = useState({
        request_type: '',
        priority: 'medium',
        description: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [checkFile, setCheckFile] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);

    // Autopay modal state
    const [showAutopayModal, setShowAutopayModal] = useState(false);
    const [autopayMethod, setAutopayMethod] = useState('bank'); // 'bank' or 'credit'
    const [autopayForm, setAutopayForm] = useState({
        bank_name: '',
        account_type: 'checking',
        account_number: '',
        routing_number: '',
        account_holder_name: '',
        // Credit card fields
        card_number: '',
        card_expiry: '',
        card_cvv: '',
        cardholder_name: ''
    });

    

    // Tenant information and document room state
    const [showEditInfoModal, setShowEditInfoModal] = useState(false);
    const [editingInfo, setEditingInfo] = useState({});
    const [documents, setDocuments] = useState([]);
    const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [documentUploadFile, setDocumentUploadFile] = useState(null);
    const [documentUploadDescription, setDocumentUploadDescription] = useState('');
    // Refs for scrolling to sections
    const paymentsSectionRef = useRef(null);
    const maintenanceSectionRef = useRef(null);
    const noticesSectionRef = useRef(null);
    const tenantInfoSectionRef = useRef(null);
    const documentRoomSectionRef = useRef(null);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    const handleMaintenanceSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        console.log('Submitting maintenance request:', maintenanceForm);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantData.tenant_id}/maintenance-request`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(maintenanceForm)
            });
            
            const data = await response.json();
            console.log('Maintenance request response:', data);
            
            if (data.success) {
                setSuccess('Maintenance request submitted successfully!');
                setMaintenanceForm({ request_type: '', priority: 'medium', description: '' });
                setTimeout(() => setSuccess(''), 3000);
                loadDashboardData();
            } else {
                setError(data.error || 'Failed to submit request');
            }
        } catch (err) {
            console.error('Error submitting maintenance request:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Load Stripe on component mount
        getStripePromise().then(setStripePromise);
        
        // Check if returning from Stripe Checkout
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment_success') === 'true') {
            setSuccess('Payment processed successfully via Stripe Checkout!');
            setTimeout(() => setSuccess(''), 5000);
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Reload dashboard data
            if (tenantData) {
                loadDashboardData();
            }
        } else if (urlParams.get('payment_cancelled') === 'true') {
            setError('Payment was cancelled.');
            setTimeout(() => setError(''), 5000);
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const handlePaymentClick = (payment) => {
        setSelectedPayment(payment);
        setShowPaymentModal(true);
        setPaymentMethod('');
        setCheckFile(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setCheckFile(file);
        }
    };

    const handleStripeCheckout = async () => {
        try {
            // Redirect to custom checkout page
            const returnPath = encodeURIComponent('/tenant-portal');
            window.location.href = `/checkout/${selectedPayment.id}?return=${returnPath}`;
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError(err.message || 'Failed to start payment. Please try again.');
            setLoading(false);
        }
    };

    const handlePaymentSubmit = async (paymentMethodId = null) => {
        if (!paymentMethod) {
            setError('Please select a payment method');
            return;
        }

        // Handle Stripe Checkout separately
        if (paymentMethod === 'Stripe Checkout') {
            await handleStripeCheckout();
            return;
        }

        setLoading(true);
        setError('');

        try {
            // If it's a check payment and we have a file, upload it first
            if (paymentMethod === 'Check' && checkFile) {
                const formData = new FormData();
                formData.append('file', checkFile);
                formData.append('description', `Check payment for tenant ${tenantData.tenant_id}`);
                
                const uploadResponse = await fetch(`${API_BASE_URL}/api/tenants/${tenantData.tenant_id}/documents`, {
                    method: 'POST',
                    body: formData
                });
                
                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload check document');
                }
            }

            // Process the payment
            const paymentData = {
                tenant_id: parseInt(tenantData.tenant_id),
                property_id: tenantData.property_id,
                amount: selectedPayment.amount,
                payment_method: paymentMethod === 'Credit' ? 'Credit Card' : paymentMethod,
                payment_method_id: paymentMethodId
            };

            const response = await fetch(`${API_BASE_URL}/api/payments/mock-submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(paymentData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to process payment');
            }

            const result = await response.json();
            
            // If payment requires confirmation (Stripe Payment Intent)
            if (result.requires_confirmation && result.client_secret) {
                // Payment will be confirmed via Stripe Elements component
                return;
            }
            
            // Update payment status to paid
            const updateResponse = await fetch(`${API_BASE_URL}/api/financial/payments/${selectedPayment.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    status: 'paid',
                    payment_method: paymentMethod === 'Credit' ? 'Credit Card' : paymentMethod,
                    reference_number: result.transaction_id,
                    paid_date: new Date().toISOString().split('T')[0]
                })
            });

            if (updateResponse.ok) {
                setSuccess('Payment processed successfully!');
                setShowPaymentModal(false);
                setSelectedPayment(null);
                setPaymentMethod('');
                setCheckFile(null);
                setTimeout(() => setSuccess(''), 3000);
                loadDashboardData();
            } else {
                setError('Failed to update payment status');
            }
        } catch (err) {
            console.error('Error processing payment:', err);
            setError(err.message || 'Payment processing failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAutopayClick = () => {
        setShowAutopayModal(true);
        // Pre-fill form with existing autopay info if available
        if (tenantData?.autopay_info) {
            const method = tenantData.autopay_info.method || tenantData?.autopay_method || 'bank';
            setAutopayMethod(method === 'credit' || method === 'card' ? 'credit' : 'bank');
            
            if (method === 'credit' || method === 'card') {
                setAutopayForm({
                    bank_name: '',
                    account_type: 'checking',
                    account_number: '',
                    routing_number: '',
                    account_holder_name: '',
                    card_number: '', // Don't pre-fill for security
                    card_expiry: tenantData.autopay_info.card_expiry || '',
                    card_cvv: '', // Don't pre-fill for security
                    cardholder_name: tenantData.autopay_info.cardholder_name || ''
                });
            } else {
                setAutopayForm({
                    bank_name: tenantData.autopay_info.bank_name || '',
                    account_type: tenantData.autopay_info.account_type || 'checking',
                    account_number: '', // Don't pre-fill for security
                    routing_number: tenantData.autopay_info.routing_number || '',
                    account_holder_name: tenantData.autopay_info.account_holder_name || '',
                    card_number: '',
                    card_expiry: '',
                    card_cvv: '',
                    cardholder_name: ''
                });
            }
        } else {
            setAutopayMethod('bank');
            setAutopayForm({
                bank_name: '',
                account_type: 'checking',
                account_number: '',
                routing_number: '',
                account_holder_name: '',
                card_number: '',
                card_expiry: '',
                card_cvv: '',
                cardholder_name: ''
            });
        }
    };

    const handleConnectBank = async () => {
        if (!stripePromise) {
            setError('Stripe is not loaded. Please refresh the page.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Create bank session
            const response = await fetch(`${API_BASE_URL}/api/stripe/create-bank-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenant_id: tenantData.tenant_id })
            });

            const { client_secret, customer_id } = await response.json();

            if (!client_secret) {
                throw new Error('Failed to create bank session');
            }

            const stripe = await stripePromise;
            const sheet = stripe.financialConnectionsSheet({
                clientSecret: client_secret,
            });

            const result = await sheet.open();

            if (result.error) {
                setError(result.error.message);
                setLoading(false);
                return;
            }

            if (result.payment_method) {
                // Save the payment method
                const saveResponse = await fetch(`${API_BASE_URL}/api/stripe/save-payment-method`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tenant_id: tenantData.tenant_id,
                        payment_method_id: result.payment_method.id,
                        payment_method_type: 'us_bank_account'
                    })
                });

                const saveData = await saveResponse.json();

                if (saveResponse.ok) {
                    setSuccess('Bank account connected successfully!');
                    setShowAutopayModal(false);
                    setTimeout(() => setSuccess(''), 3000);
                    loadDashboardData();
            } else {
                    setError(saveData.error || 'Failed to save payment method');
                }
            }
        } catch (err) {
            console.error('Error connecting bank:', err);
            setError(err.message || 'Failed to connect bank account');
        } finally {
            setLoading(false);
        }
    };

    const handleAutopaySubmit = async (paymentMethodId, paymentMethodType = 'card') => {
        setLoading(true);
        setError('');

        try {
            // Save the payment method
            const response = await fetch(`${API_BASE_URL}/api/stripe/save-payment-method`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenant_id: tenantData.tenant_id,
                    payment_method_id: paymentMethodId,
                    payment_method_type: paymentMethodType
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess('Autopay setup successfully!');
                setShowAutopayModal(false);
                setTimeout(() => setSuccess(''), 3000);
                loadDashboardData();
            } else {
                setError(data.error || 'Failed to setup autopay');
            }
        } catch (err) {
            console.error('Error setting up autopay:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivateAutopay = async () => {
        if (!window.confirm('Are you sure you want to deactivate autopay?')) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantData.tenant_id}/autopay`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess('Autopay deactivated successfully!');
                setTimeout(() => setSuccess(''), 3000);
                loadDashboardData();
            } else {
                setError(data.error || 'Failed to deactivate autopay');
            }
        } catch (err) {
            console.error('Error deactivating autopay:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchDocuments = async () => {
        if (!tenantData?.tenant_id) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantData.tenant_id}/documents`);
            if (response.ok) {
                const data = await response.json();
                setDocuments(data);
            }
        } catch (err) {
            console.error('Error fetching documents:', err);
        }
    };

    const handleEditInfo = () => {
        // Pre-fill editable fields
        setEditingInfo({
            'First Name': tenantData?.first_name || '',
            'Last Name': tenantData?.last_name || '',
            'Email': tenantData?.email || '',
            'Phone': tenantData?.phone || '',
            'Emergency Contact Name': tenantData?.emergency_contact_name || '',
            'Emergency Contact Phone': tenantData?.emergency_contact_phone || '',
            'Emergency Contact Relationship': tenantData?.emergency_contact_relationship || '',
            'Employment Status': tenantData?.employment_status || '',
            'Employer Name': tenantData?.employer_name || '',
            'Monthly Income': tenantData?.monthly_income || ''
        });
        setShowEditInfoModal(true);
    };

    const handleSaveInfo = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantData.tenant_id}/self-update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingInfo)
            });

            if (response.ok) {
                setSuccess('Information updated successfully!');
                setShowEditInfoModal(false);
                setTimeout(() => setSuccess(''), 3000);
                loadDashboardData(); // Reload to get updated data
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update information');
            }
        } catch (err) {
            setError(err.message || 'Failed to update information');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentUpload = async () => {
        if (!documentUploadFile) {
            setError('Please select a file to upload');
            return;
        }

        setUploadingDocument(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', documentUploadFile);
            formData.append('description', documentUploadDescription || 'Document uploaded by tenant');

            const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantData.tenant_id}/documents`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSuccess('Document uploaded successfully!');
                setShowDocumentUploadModal(false);
                setDocumentUploadFile(null);
                setDocumentUploadDescription('');
                setTimeout(() => setSuccess(''), 3000);
                fetchDocuments(); // Reload documents
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to upload document');
            }
        } catch (err) {
            setError(err.message || 'Failed to upload document');
        } finally {
            setUploadingDocument(false);
        }
    };

    const handleViewDocument = (document) => {
        // Open document in new tab
        window.open(`${API_BASE_URL}/api/documents/${document.id}/download`, '_blank');
    };

    const loadDashboardData = async () => {
        if (!tenantData) return;
        
        try {
            console.log('Loading dashboard data for tenant:', tenantData.tenant_id);
            const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantData.tenant_id}/dashboard`);
            const data = await response.json();
            
            if (response.ok) {
                console.log('Dashboard data loaded successfully:', data);
                
                                // Properly merge the dashboard data with existing tenant data
                setTenantData(prev => {
                    const updatedData = {
                        ...prev,
                        ...data.tenant_info, // Spread tenant info
                        upcoming_payments: data.upcoming_payments || [],
                        maintenance_requests: data.maintenance_requests || [],
                        notices: data.notices || [],
                        autopay_info: data.autopay_info || null,
                        // Add autopay configuration flags
                        'Autopay Enabled': Boolean(data.tenant_info.autopay_enabled),
                        'Autopay Method': data.tenant_info.autopay_method
                    };
                    
                    console.log('Previous tenant data:', prev);
                    console.log('New dashboard data:', data);
                    console.log('Updated tenant data:', updatedData);
                    
                    return updatedData;
                });
            } else {
                console.error('Failed to load dashboard data:', data);
            }
        } catch (err) {
            console.error('Error loading dashboard data:', err);
        }
    };

    // Scroll to section functions
    const scrollToSection = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
            });
        }
    };

    const handleActionClick = (action) => {
        console.log('Action clicked:', action);
        switch (action) {
            case 'payments':
                scrollToSection(paymentsSectionRef);
                break;
            case 'maintenance':
                scrollToSection(maintenanceSectionRef);
                break;
            case 'notices':
                scrollToSection(noticesSectionRef);
                break;
            case 'info':
                scrollToSection(tenantInfoSectionRef);
                break;
            case 'documents':
                scrollToSection(documentRoomSectionRef);
                break;
            case 'history':
                // Scroll to both payments and maintenance sections
                scrollToSection(paymentsSectionRef);
                setTimeout(() => {
                    scrollToSection(maintenanceSectionRef);
                }, 1000);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        // Check if user is authenticated and is a tenant
        const isAuthenticated = localStorage.getItem('authenticated') === 'true';
        const userType = localStorage.getItem('userType');
        
        if (!isAuthenticated || userType !== 'tenant') {
            window.location.href = '/';
            return;
        }

        // Load tenant data from localStorage
        const storedTenantData = localStorage.getItem('tenantData');
        if (storedTenantData) {
            const parsedData = JSON.parse(storedTenantData);
            setTenantData(parsedData);
        } else {
            // If no tenant data, redirect to login
            window.location.href = '/';
        }
    }, []); // Only run once on component mount

    // Separate useEffect to load dashboard data when tenantData is available
    useEffect(() => {
        if (tenantData && tenantData.tenant_id) {
            console.log('Loading dashboard data for tenant:', tenantData.tenant_id);
            loadDashboardData();
        }
    }, [tenantData?.tenant_id]); // Run when tenant_id changes

    // If no tenant data yet, show loading
    if (!tenantData) {
        return (
            <div className="tenant-portal-loading">
                <div className="loading-card">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tenant-portal">
            <header className="portal-header">
                <div className="header-content">
                    <div className="header-left">
                        <h1>🏠 Tenant Portal</h1>
                        <div className="tenant-welcome">
                            <span>Welcome back, {tenantData?.first_name} {tenantData?.last_name}</span>
                            <span className="tenant-code">Tenant Code: {tenantData?.tenant_code}</span>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="logout-btn">
                        <span>🚪</span> Logout
                    </button>
                </div>
            </header>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <main className="portal-content">
                {/* Tenant Information Section */}
                <section ref={tenantInfoSectionRef} className="portal-section tenant-info-section">
                    <div className="section-header">
                        <h2>👤 My Information</h2>
                        <button onClick={handleEditInfo} className="edit-info-btn" disabled={loading}>
                            ✏️ Edit Personal Info
                        </button>
                    </div>
                    <div className="info-grid">
                        <div className="info-card">
                            <h3>Personal Information</h3>
                            <div className="info-item">
                                <span className="label">First Name:</span>
                                <span className="value">{tenantData?.first_name || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Last Name:</span>
                                <span className="value">{tenantData?.last_name || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Email:</span>
                                <span className="value">{tenantData?.email || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Phone:</span>
                                <span className="value">{tenantData?.phone || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Tenant Code:</span>
                                <span className="value">{tenantData?.tenant_code || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="info-card">
                            <h3>Emergency Contact</h3>
                            <div className="info-item">
                                <span className="label">Name:</span>
                                <span className="value">{tenantData?.emergency_contact_name || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Phone:</span>
                                <span className="value">{tenantData?.emergency_contact_phone || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Relationship:</span>
                                <span className="value">{tenantData?.emergency_contact_relationship || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="info-card">
                            <h3>Employment Information</h3>
                            <div className="info-item">
                                <span className="label">Employment Status:</span>
                                <span className="value">{tenantData?.employment_status || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Employer:</span>
                                <span className="value">{tenantData?.employer_name || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Monthly Income:</span>
                                <span className="value">{tenantData?.monthly_income ? `$${tenantData.monthly_income}` : 'N/A'}</span>
                            </div>
                        </div>

                        <div className="info-card frozen-info">
                            <h3>Leasing Information <span className="frozen-badge">🔒 Locked</span></h3>
                            <div className="info-item">
                                <span className="label">Property:</span>
                                <span className="value">{tenantData?.property_name || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Unit Address:</span>
                                <span className="value">{tenantData?.unit_address || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Monthly Rent:</span>
                                <span className="value rent-amount">${tenantData?.monthly_rent || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Lease Status:</span>
                                <span className="value">{tenantData?.lease_status || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Move-Out Date:</span>
                                <span className="value">{tenantData?.move_out_date || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Credit Score:</span>
                                <span className="value">{tenantData?.credit_score || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Security Deposit:</span>
                                <span className="value">{tenantData?.security_deposit_amount ? `$${tenantData.security_deposit_amount}` : 'N/A'}</span>
                            </div>
                            <p className="frozen-note">💡 Leasing details are managed by property management. Contact your landlord to update.</p>
                        </div>
                    </div>
                </section>

                {/* Quick Actions Section */}
                <section className="portal-section quick-actions">
                    <div className="section-header">
                        <h2>⚡ Quick Actions</h2>
                    </div>
                    <div className="actions-grid">
                        <div className="action-card" onClick={() => handleActionClick('payments')}>
                            <div className="action-icon">💳</div>
                            <h3>Make Payment</h3>
                            <p>Pay your rent online</p>
                        </div>
                        <div className="action-card" onClick={() => handleActionClick('maintenance')}>
                            <div className="action-icon">🔧</div>
                            <h3>Maintenance</h3>
                            <p>Submit a request</p>
                        </div>
                        <div className="action-card" onClick={() => handleActionClick('history')}>
                            <div className="action-icon">📋</div>
                            <h3>View History</h3>
                            <p>Payment & request history</p>
                        </div>
                        <div className="action-card" onClick={() => handleActionClick('notices')}>
                            <div className="action-icon">📢</div>
                            <h3>Notices</h3>
                            <p>Management updates</p>
                        </div>
                        <div className="action-card" onClick={() => handleActionClick('info')}>
                            <div className="action-icon">👤</div>
                            <h3>My Information</h3>
                            <p>View & edit profile</p>
                        </div>
                        <div className="action-card" onClick={() => handleActionClick('documents')}>
                            <div className="action-icon">📁</div>
                            <h3>Document Room</h3>
                            <p>Leases & documents</p>
                        </div>
                    </div>
                </section>

                                 {/* Upcoming Payments Section */}
                 <section ref={paymentsSectionRef} className="portal-section payments-section">
                     <div className="section-header">
                         <h2>💰 Upcoming Payments</h2>
                         <div className="section-actions">
                             <button 
                                 onClick={handleAutopayClick}
                                 className="autopay-btn"
                                 disabled={loading}
                             >
                                 {(tenantData?.autopay_info || tenantData?.['Autopay Enabled']) ? '🔄 Update Autopay' : '⚡ Activate Autopay'}
                             </button>
                             {(tenantData?.autopay_info || tenantData?.['Autopay Enabled']) && (
                                 <button 
                                     onClick={handleDeactivateAutopay}
                                     className="deactivate-autopay-btn"
                                     disabled={loading}
                                 >
                                     🚫 Deactivate Autopay
                                 </button>
                             )}
                         </div>
                     </div>
                     {console.log('Rendering payments section, upcoming_payments:', tenantData?.upcoming_payments)}
                     {tenantData?.upcoming_payments && tenantData.upcoming_payments.length > 0 ? (
                         <div className="payments-grid">
                             {tenantData.upcoming_payments.slice(0, 6).map((payment, index) => (
                                 <div 
                                     key={index} 
                                     className={`payment-card ${payment.status} clickable`}
                                     onClick={() => handlePaymentClick(payment)}
                                     style={{ cursor: 'pointer' }}
                                 >
                                     <div className="payment-header">
                                         <h4>Rent Payment</h4>
                                         <span className={`status ${payment.status}`}>{payment.status}</span>
                                     </div>
                                     <div className="payment-details">
                                         <div className="amount">${payment.amount}</div>
                                         <div className="due-date">Due: {payment.due_date}</div>
                                         <div className="month">{new Date(payment.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                                     </div>
                                     <div className="payment-action-hint">
                                         Click to process payment
                                     </div>
                                 </div>
                             ))}
                         </div>
                     ) : (
                         <div className="empty-state">
                             <div className="empty-icon">✅</div>
                             <h3>All Caught Up!</h3>
                             <p>No upcoming payments at this time.</p>
                         </div>
                     )}
                 </section>

                 {/* Current Autopay Information Section */}
                 {(tenantData?.autopay_info || tenantData?.['Autopay Enabled']) && (
                     <section className="portal-section autopay-section">
                         <div className="section-header">
                             <h2>💳 Current Autopay Setup</h2>
                         </div>
                         <div className="autopay-display">
                             <div className="autopay-card">
                                 <div className="autopay-header">
                                     <div className="bank-info">
                                         <h3>
                                             {(() => {
                                                 const method = tenantData.autopay_info?.method || tenantData?.autopay_method;
                                                 if (method === 'credit' || method === 'card') {
                                                     return '💳 Credit/Debit Card';
                                                 }
                                                 return tenantData.autopay_info?.bank_name || '🏦 Autopay Setup';
                                             })()}
                                         </h3>
                                         <span className="account-type">
                                             {(() => {
                                                 const method = tenantData.autopay_info?.method || tenantData?.autopay_method;
                                                 if (method === 'credit' || method === 'card') {
                                                     return 'Credit/Debit Card';
                                                 }
                                                 return tenantData.autopay_info?.account_type || 'Bank Account';
                                             })()}
                                         </span>
                                     </div>
                                     <div className="autopay-status">
                                         <span className="status-badge active">✅ Active</span>
                                     </div>
                                 </div>
                                 <div className="autopay-details">
                                     {tenantData.autopay_info ? (
                                         <>
                                             {(() => {
                                                 const method = tenantData.autopay_info.method || tenantData?.autopay_method;
                                                 if (method === 'credit' || method === 'card') {
                                                     return (
                                                         <>
                                                             <div className="detail-row">
                                                                 <span className="label">Card Number:</span>
                                                                 <span className="value">****{tenantData.autopay_info.card_number || '****'}</span>
                                                             </div>
                                                             <div className="detail-row">
                                                                 <span className="label">Expiry Date:</span>
                                                                 <span className="value">{tenantData.autopay_info.card_expiry || 'N/A'}</span>
                                                             </div>
                                                             <div className="detail-row">
                                                                 <span className="label">Cardholder:</span>
                                                                 <span className="value">{tenantData.autopay_info.cardholder_name || 'N/A'}</span>
                                                             </div>
                                                         </>
                                                     );
                                                 }
                                                 return (
                                                     <>
                                                         <div className="detail-row">
                                                             <span className="label">Account Number:</span>
                                                             <span className="value">****{tenantData.autopay_info.account_number || '****'}</span>
                                                         </div>
                                                         <div className="detail-row">
                                                             <span className="label">Routing Number:</span>
                                                             <span className="value">{tenantData.autopay_info.routing_number || 'N/A'}</span>
                                                         </div>
                                                         <div className="detail-row">
                                                             <span className="label">Account Holder:</span>
                                                             <span className="value">{tenantData.autopay_info.account_holder_name || 'N/A'}</span>
                                                         </div>
                                                     </>
                                                 );
                                             })()}
                                             {tenantData.autopay_info.created_at && (
                                                 <div className="detail-row">
                                                     <span className="label">Setup Date:</span>
                                                     <span className="value">{new Date(tenantData.autopay_info.created_at).toLocaleDateString()}</span>
                                                 </div>
                                             )}
                                         </>
                                     ) : (
                                         <div className="detail-row">
                                             <span className="label">Status:</span>
                                             <span className="value">Autopay is enabled but details need to be configured</span>
                                         </div>
                                     )}
                                 </div>
                                 <div className="autopay-note">
                                     <p>💡 Your rent will be automatically charged on the due date.</p>
                                 </div>
                             </div>
                         </div>
                     </section>
                 )}

                

                {/* Maintenance Section */}
                <section ref={maintenanceSectionRef} className="portal-section maintenance-section">
                    <div className="section-header">
                        <h2>🔧 Maintenance Requests</h2>
                    </div>
                    
                    <div className="maintenance-content">
                        <div className="maintenance-form-card">
                            <h3>Submit New Request</h3>
                            <form onSubmit={handleMaintenanceSubmit} className="maintenance-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Request Type:</label>
                                        <select
                                            value={maintenanceForm.request_type}
                                            onChange={(e) => setMaintenanceForm({...maintenanceForm, request_type: e.target.value})}
                                            required
                                        >
                                            <option value="">Select request type</option>
                                            <option value="plumbing">🚰 Plumbing</option>
                                            <option value="electrical">⚡ Electrical</option>
                                            <option value="hvac">❄️ HVAC</option>
                                            <option value="appliance">🔌 Appliance</option>
                                            <option value="structural">🏗️ Structural</option>
                                            <option value="pest_control">🐜 Pest Control</option>
                                            <option value="other">📝 Other</option>
                                        </select>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Priority:</label>
                                        <select
                                            value={maintenanceForm.priority}
                                            onChange={(e) => setMaintenanceForm({...maintenanceForm, priority: e.target.value})}
                                            required
                                        >
                                            <option value="low">🟢 Low</option>
                                            <option value="medium">🟡 Medium</option>
                                            <option value="high">🔴 High</option>
                                            <option value="emergency">🚨 Emergency</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label>Description:</label>
                                    <textarea
                                        value={maintenanceForm.description}
                                        onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                                        required
                                        placeholder="Please describe the issue in detail..."
                                        rows="4"
                                    />
                                </div>
                                
                                <button type="submit" disabled={loading}>
                                    {loading ? 'Submitting...' : '📤 Submit Request'}
                                </button>
                            </form>
                        </div>
                        
                        <div className="maintenance-history">
                            <h3>Recent Requests</h3>
                            {tenantData?.maintenance_requests && tenantData.maintenance_requests.length > 0 ? (
                                <div className="maintenance-list">
                                    {tenantData.maintenance_requests.slice(0, 5).map((req, index) => (
                                        <div key={index} className="maintenance-request-card">
                                            <div className="request-header">
                                                <h4>{req.request_type}</h4>
                                                <div className="request-badges">
                                                    <span className={`priority ${req.priority}`}>{req.priority}</span>
                                                    <span className={`status ${req.status}`}>{req.status}</span>
                                                </div>
                                            </div>
                                            <p className="request-description">{req.description}</p>
                                            <small className="request-date">Submitted: {req.created_at}</small>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-icon">📋</div>
                                    <h3>No Requests Yet</h3>
                                    <p>Submit your first maintenance request above.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Notices Section */}
                <section ref={noticesSectionRef} className="portal-section notices-section">
                    <div className="section-header">
                        <h2>📢 Notices from Management</h2>
                    </div>
                    
                    {tenantData?.notices && tenantData.notices.length > 0 ? (
                        <div className="notices-grid">
                            {tenantData.notices.map((notice, index) => (
                                <div key={index} className={`notice-card ${notice.priority}`}>
                                    <div className="notice-header">
                                        <h3>{notice.title}</h3>
                                        <span className={`priority ${notice.priority}`}>{notice.priority}</span>
                                    </div>
                                    <p>{notice.message}</p>
                                    <small>Posted: {notice.created_at}</small>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-icon">📭</div>
                            <h3>No Notices</h3>
                            <p>No notices from management at this time.</p>
                        </div>
                                         )}
                 </section>

                 {/* Document Room Section */}
                 <section ref={documentRoomSectionRef} className="portal-section document-room-section">
                     <div className="section-header">
                         <h2>📁 Document Room</h2>
                         <button 
                             onClick={() => setShowDocumentUploadModal(true)}
                             className="upload-document-btn"
                             disabled={loading || uploadingDocument}
                         >
                             📤 Upload Document
                         </button>
                     </div>
                     
                     {documents && documents.length > 0 ? (
                         <div className="documents-grid">
                             {documents.map((doc) => (
                                 <div key={doc.id} className="document-card">
                                     <div className="document-icon">
                                         {doc.file_type?.includes('pdf') ? '📄' : 
                                          doc.file_type?.includes('image') ? '🖼️' : '📎'}
                                     </div>
                                     <div className="document-info">
                                         <h4>{doc.original_filename || doc.filename}</h4>
                                         <p className="document-description">{doc.description || 'No description'}</p>
                                         <div className="document-meta">
                                             <span className="document-date">
                                                 {new Date(doc.upload_date).toLocaleDateString()}
                                             </span>
                                             {doc.file_size && (
                                                 <span className="document-size">
                                                     {(doc.file_size / 1024).toFixed(1)} KB
                                                 </span>
                                             )}
                                         </div>
                                     </div>
                                     <div className="document-actions">
                                         <button 
                                             onClick={() => handleViewDocument(doc)}
                                             className="view-document-btn"
                                         >
                                             👁️ View
                                         </button>
                                     </div>
                                 </div>
                             ))}
                         </div>
                     ) : (
                         <div className="empty-state">
                             <div className="empty-icon">📁</div>
                             <h3>No Documents Yet</h3>
                             <p>Upload documents such as leases, receipts, or other important files.</p>
                        </div>
                                         )}
                 </section>
             </main>

                           {/* Autopay Modal */}
              {showAutopayModal && tenantData?.tenant_id && (
                  <StripeAutopaySetup
                      tenantId={tenantData.tenant_id}
                      tenantName={`${tenantData.first_name || ''} ${tenantData.last_name || ''}`.trim()}
                      tenantEmail={tenantData.email}
                      onSuccess={(data) => {
                          setShowAutopayModal(false);
                          if (data.deactivated) {
                              setSuccess('Autopay deactivated successfully!');
                          } else {
                              setSuccess('Autopay setup successfully!');
                          }
                          setTimeout(() => setSuccess(''), 3000);
                          loadDashboardData();
                      }}
                      onClose={() => setShowAutopayModal(false)}
                      mode="tenant"
                  />
              )}

             {/* Payment Modal */}
             {showPaymentModal && selectedPayment && (
                 <div className="modal-overlay">
                     <div className="payment-modal">
                         <div className="modal-header">
                             <h2>💳 Process Payment</h2>
                             <button 
                                 onClick={() => setShowPaymentModal(false)}
                                 className="close-btn"
                             >
                                 ✕
                             </button>
                         </div>
                         
                         <div className="modal-content">
                             <div className="payment-summary">
                                 <h3>Payment Details</h3>
                                 <div className="payment-info">
                                     <p><strong>Amount:</strong> ${selectedPayment.amount}</p>
                                     <p><strong>Due Date:</strong> {selectedPayment.due_date}</p>
                                     <p><strong>Status:</strong> <span className={`status ${selectedPayment.status}`}>{selectedPayment.status}</span></p>
                                 </div>
                             </div>

                             <div className="payment-method-selection">
                                 <h3>Select Payment Method</h3>
                                 <div className="payment-options">
                                     <label className="payment-option">
                                         <input
                                             type="radio"
                                             name="paymentMethod"
                                             value="Stripe Checkout"
                                             checked={paymentMethod === 'Stripe Checkout'}
                                             onChange={(e) => setPaymentMethod(e.target.value)}
                                         />
                                         <span className="option-content">
                                             <span className="option-icon">🔒</span>
                                             <span className="option-text">Stripe Checkout (Card or Bank)</span>
                                         </span>
                                     </label>
                                     
                                     <label className="payment-option">
                                         <input
                                             type="radio"
                                             name="paymentMethod"
                                             value="Credit"
                                             checked={paymentMethod === 'Credit'}
                                             onChange={(e) => setPaymentMethod(e.target.value)}
                                         />
                                         <span className="option-content">
                                             <span className="option-icon">💳</span>
                                             <span className="option-text">Credit Card (Inline)</span>
                                         </span>
                                     </label>
                                     
                                     <label className="payment-option">
                                         <input
                                             type="radio"
                                             name="paymentMethod"
                                             value="Check"
                                             checked={paymentMethod === 'Check'}
                                             onChange={(e) => setPaymentMethod(e.target.value)}
                                         />
                                         <span className="option-content">
                                             <span className="option-icon">🏦</span>
                                             <span className="option-text">Check</span>
                                         </span>
                                     </label>
                                     
                                     <label className="payment-option">
                                         <input
                                             type="radio"
                                             name="paymentMethod"
                                             value="Cash"
                                             checked={paymentMethod === 'Cash'}
                                             onChange={(e) => setPaymentMethod(e.target.value)}
                                         />
                                         <span className="option-content">
                                             <span className="option-icon">💰</span>
                                             <span className="option-text">Cash</span>
                                         </span>
                                     </label>
                                 </div>
                             </div>

                             {paymentMethod === 'Stripe Checkout' && (
                                 <div className="stripe-checkout-info">
                                     <h3>🔒 Secure Payment via Stripe Checkout</h3>
                                     <p style={{ marginBottom: '20px', color: '#666' }}>
                                         You'll be redirected to Stripe's secure payment page where you can pay with a credit card or bank account.
                                     </p>
                                     <button
                                         type="button"
                                         onClick={handleStripeCheckout}
                                         className="stripe-checkout-btn"
                                         disabled={loading}
                                         style={{
                                             width: '100%',
                                             padding: '12px',
                                             backgroundColor: '#635BFF',
                                             color: 'white',
                                             border: 'none',
                                             borderRadius: '6px',
                                             fontSize: '16px',
                                             cursor: loading ? 'not-allowed' : 'pointer',
                                             opacity: loading ? 0.6 : 1
                                         }}
                                     >
                                         {loading ? 'Redirecting...' : `Pay $${selectedPayment.amount.toFixed(2)} via Stripe Checkout`}
                                     </button>
                                 </div>
                             )}

                             {paymentMethod === 'Credit' && stripePromise && (
                                 <div className="credit-card-form">
                                     <h3>Credit Card Information</h3>
                                     <Elements stripe={stripePromise}>
                                         <StripePaymentForm
                                             paymentId={selectedPayment.id}
                                             amount={selectedPayment.amount}
                                             onSuccess={(paymentIntent) => {
                                                 setSuccess('Payment processed successfully!');
                                                 setShowPaymentModal(false);
                                                 setSelectedPayment(null);
                                                 setPaymentMethod('');
                                                 setTimeout(() => setSuccess(''), 3000);
                                                 loadDashboardData();
                                             }}
                                             onError={(error) => {
                                                 setError(error);
                                             }}
                                         />
                                     </Elements>
                                 </div>
                             )}
                             {paymentMethod === 'Credit' && !stripePromise && (
                                 <div className="credit-card-form">
                                     <p>Loading payment form...</p>
                                 </div>
                             )}

                             {paymentMethod === 'Check' && (
                                 <div className="check-form">
                                     <h3>Check Upload</h3>
                                         <div className="form-group">
                                         <label>Upload Scanned Check:</label>
                                         <input
                                             type="file"
                                             accept="image/*,.pdf"
                                             onChange={handleFileChange}
                                             className="file-input"
                                         />
                                         {checkFile && (
                                             <p className="file-selected">Selected: {checkFile.name}</p>
                                         )}
                                     </div>
                                 </div>
                             )}

                             <div className="modal-actions">
                                 <button 
                                     onClick={() => setShowPaymentModal(false)}
                                     className="cancel-btn"
                                     disabled={loading}
                                 >
                                     Cancel
                                 </button>
                                 {paymentMethod !== 'Credit' && paymentMethod !== 'Stripe Checkout' && (
                                     <button 
                                         onClick={() => handlePaymentSubmit()}
                                         className="submit-btn"
                                         disabled={loading || !paymentMethod}
                                     >
                                         {loading ? 'Processing...' : 'Process Payment'}
                                     </button>
                                 )}
                             </div>
                         </div>
                     </div>
                 </div>
             )}

             {/* Edit Information Modal */}
             {showEditInfoModal && (
                 <div className="modal-overlay">
                     <div className="payment-modal">
                         <div className="modal-header">
                             <h2>✏️ Edit Personal Information</h2>
                             <button 
                                 onClick={() => setShowEditInfoModal(false)}
                                 className="close-btn"
                             >
                                 ✕
                             </button>
                         </div>
                         
                         <div className="modal-content">
                             <p className="edit-note" style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                                 💡 You can only edit personal information. Leasing details are managed by property management.
                             </p>
                             
                             <div className="form-group">
                                 <label>First Name:</label>
                                             <input
                                                 type="text"
                                     value={editingInfo['First Name'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'First Name': e.target.value})}
                                             />
                                         </div>
                             
                                         <div className="form-group">
                                 <label>Last Name:</label>
                                             <input
                                                 type="text"
                                     value={editingInfo['Last Name'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Last Name': e.target.value})}
                                             />
                                         </div>
                             
                             <div className="form-group">
                                 <label>Email:</label>
                                 <input
                                     type="email"
                                     value={editingInfo['Email'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Email': e.target.value})}
                                 />
                                     </div>
                             
                                         <div className="form-group">
                                 <label>Phone:</label>
                                 <input
                                     type="tel"
                                     value={editingInfo['Phone'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Phone': e.target.value})}
                                 />
                             </div>
                             
                             <div className="form-group">
                                 <label>Emergency Contact Name:</label>
                                             <input
                                                 type="text"
                                     value={editingInfo['Emergency Contact Name'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Emergency Contact Name': e.target.value})}
                                             />
                                         </div>
                             
                                         <div className="form-group">
                                 <label>Emergency Contact Phone:</label>
                                 <input
                                     type="tel"
                                     value={editingInfo['Emergency Contact Phone'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Emergency Contact Phone': e.target.value})}
                                 />
                             </div>
                             
                             <div className="form-group">
                                 <label>Emergency Contact Relationship:</label>
                                             <input
                                                 type="text"
                                     value={editingInfo['Emergency Contact Relationship'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Emergency Contact Relationship': e.target.value})}
                                             />
                                         </div>
                             
                             <div className="form-group">
                                 <label>Employment Status:</label>
                                 <select
                                     value={editingInfo['Employment Status'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Employment Status': e.target.value})}
                                 >
                                     <option value="">Select...</option>
                                     <option value="employed">Employed</option>
                                     <option value="self-employed">Self-Employed</option>
                                     <option value="unemployed">Unemployed</option>
                                     <option value="retired">Retired</option>
                                     <option value="student">Student</option>
                                 </select>
                                     </div>
                             
                                     <div className="form-group">
                                 <label>Employer Name:</label>
                                         <input
                                             type="text"
                                     value={editingInfo['Employer Name'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Employer Name': e.target.value})}
                                 />
                             </div>
                             
                             <div className="form-group">
                                 <label>Monthly Income:</label>
                                 <input
                                     type="number"
                                     value={editingInfo['Monthly Income'] || ''}
                                     onChange={(e) => setEditingInfo({...editingInfo, 'Monthly Income': e.target.value})}
                                 />
                             </div>
                             
                             <div className="modal-actions">
                                 <button 
                                     onClick={() => setShowEditInfoModal(false)}
                                     className="cancel-btn"
                                     disabled={loading}
                                 >
                                     Cancel
                                 </button>
                                 <button 
                                     onClick={handleSaveInfo}
                                     className="submit-btn"
                                     disabled={loading}
                                 >
                                     {loading ? 'Saving...' : 'Save Changes'}
                                 </button>
                             </div>
                         </div>
                                     </div>
                                 </div>
                             )}

             {/* Document Upload Modal */}
             {showDocumentUploadModal && (
                 <div className="modal-overlay">
                     <div className="payment-modal">
                         <div className="modal-header">
                             <h2>📤 Upload Document</h2>
                             <button 
                                 onClick={() => {
                                     setShowDocumentUploadModal(false);
                                     setDocumentUploadFile(null);
                                     setDocumentUploadDescription('');
                                 }}
                                 className="close-btn"
                             >
                                 ✕
                             </button>
                         </div>
                         
                         <div className="modal-content">
                             <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                                 Upload documents such as leases, receipts, identification, or other important files.
                             </p>
                             
                                     <div className="form-group">
                                 <label>Select File:</label>
                                         <input
                                             type="file"
                                     onChange={(e) => setDocumentUploadFile(e.target.files[0])}
                                     accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                                 />
                                 {documentUploadFile && (
                                     <p className="file-selected" style={{ marginTop: '10px', color: '#666' }}>
                                         Selected: {documentUploadFile.name}
                                     </p>
                                         )}
                                     </div>
                             
                             <div className="form-group">
                                 <label>Description (optional):</label>
                                 <textarea
                                     value={documentUploadDescription}
                                     onChange={(e) => setDocumentUploadDescription(e.target.value)}
                                     placeholder="e.g., Lease Agreement, Receipt, ID Copy..."
                                     rows="3"
                                 />
                                 </div>

                             <div className="modal-actions">
                                 <button 
                                     onClick={() => {
                                         setShowDocumentUploadModal(false);
                                         setDocumentUploadFile(null);
                                         setDocumentUploadDescription('');
                                     }}
                                     className="cancel-btn"
                                     disabled={uploadingDocument}
                                 >
                                     Cancel
                                 </button>
                                 <button 
                                     onClick={handleDocumentUpload}
                                     className="submit-btn"
                                     disabled={uploadingDocument || !documentUploadFile}
                                 >
                                     {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                                 </button>
                             </div>
                         </div>
                     </div>
                 </div>
             )}
         </div>
     );
 };

export default TenantPortal;
